// "Is this device fully set up?" — installed as an app, and allowed to show
// notifications. Drives the persistent setup card in the sidebar
// (components/Home/AppSetupCard.js), the Odoo-style nag that stays until both
// are done instead of a one-off popup a user dismisses and never sees again.
//
// Imported from index.js so the `beforeinstallprompt` listener is attached at
// startup: Chrome fires that event once, early, per page load, and a listener
// added later (when the sidebar mounts after login) would miss it and leave
// the Install button with nothing to call.
import { useEffect, useState } from 'react';
import { ensurePushSubscription, isPushSupported } from '../realtime/pushClient';

let deferredInstallPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault(); // keep it for our own button instead of Chrome's mini-infobar
        deferredInstallPrompt = e;
        notify();
    });
    window.addEventListener('appinstalled', () => {
        deferredInstallPrompt = null;
        notify();
    });
    // A user can change the permission in browser settings, or install from
    // the browser menu, while the app is open — re-read on return.
    window.addEventListener('focus', notify);
    document.addEventListener('visibilitychange', notify);
    navigator.permissions?.query?.({ name: 'notifications' })
        .then((status) => { status.onchange = notify; })
        .catch(() => {});
}

export function isInstalled() {
    return ['standalone', 'fullscreen', 'minimal-ui'].some(
        (mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches
    ) || window.navigator.standalone === true; // iOS Safari
}

// iPadOS reports itself as a Mac; the touch check tells them apart.
export function isIOS() {
    const ua = window.navigator.userAgent;
    return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

/** 'granted' | 'default' | 'denied' | 'unsupported' */
export function notificationState() {
    return isPushSupported() ? Notification.permission : 'unsupported';
}

export function getAppSetupStatus() {
    const installed = isInstalled();
    const ios = isIOS();
    const notifications = notificationState();
    return {
        installed,
        ios,
        canPromptInstall: !!deferredInstallPrompt,
        notifications,
        // iOS only exposes the Notification API to a home-screen app, so in a
        // Safari tab it reads "unsupported" — that is "install first", not "never".
        notificationsNeedInstall: ios && !installed && notifications === 'unsupported',
        // Done when installed AND notifications are on — or can never be on
        // here (an old browser), which no button on this card can fix.
        complete: installed && (notifications === 'granted' || notifications === 'unsupported')
    };
}

export async function promptInstall() {
    if (!deferredInstallPrompt) return false;
    const event = deferredInstallPrompt;
    deferredInstallPrompt = null; // a prompt event can only be used once
    event.prompt();
    const { outcome } = await event.userChoice;
    notify();
    return outcome === 'accepted';
}

// Must run from a click: browsers ignore or quietly block permission requests
// that are not tied to a user gesture.
export async function enableNotifications() {
    const result = await ensurePushSubscription();
    notify();
    return result;
}

export function useAppSetupStatus() {
    const [status, setStatus] = useState(getAppSetupStatus);
    useEffect(() => {
        const update = () => setStatus(getAppSetupStatus());
        listeners.add(update);
        update();
        return () => listeners.delete(update);
    }, []);
    return status;
}
